/**
 * Converts most non-color variation <select> elements on the
 * single-product page (woocommerce/single-product/add-to-cart/variable.php
 * — e.g. "Size") into a row of clickable pill/radio buttons. Excluded:
 * pa_color (gets circular color swatches instead, see color-swatches.js)
 * and pa_embroidery-type (Type of Embroidery can carry all 16 terms on
 * one product, which reads as a cramped multi-row wall of pills — kept
 * as the plain styled dropdown from _single-product.scss's own
 * `select` rules instead).
 *
 * Same pattern as color-swatches.js: the underlying <select> is kept,
 * just visually hidden, and stays the real source of truth WooCommerce's
 * own variation.js reads from/updates. Clicking a pill sets the select's
 * value and dispatches a native "change" event rather than reimplementing
 * any price/stock/variation-matching logic.
 */

function buildPills( select ) {
	const wrapper = select.closest( 'td.value' ) || select.parentElement;
	if ( ! wrapper || wrapper.querySelector( '.craftis-variation-pills' ) ) {
		return;
	}

	const options = Array.from( select.options ).filter( ( option ) => option.value !== '' );

	if ( ! options.length ) {
		return;
	}

	const pillList = document.createElement( 'div' );
	pillList.className = 'craftis-variation-pills';
	pillList.setAttribute( 'role', 'radiogroup' );

	options.forEach( ( option ) => {
		const pill = document.createElement( 'button' );
		pill.type = 'button';
		pill.className = 'craftis-variation-pills__item';
		pill.setAttribute( 'role', 'radio' );
		pill.setAttribute( 'aria-checked', option.selected ? 'true' : 'false' );
		pill.dataset.value = option.value;
		pill.textContent = option.textContent.trim();

		if ( option.disabled ) {
			pill.disabled = true;
			pill.classList.add( 'is-disabled' );
		}

		if ( option.selected ) {
			pill.classList.add( 'is-selected' );
		}

		pill.addEventListener( 'click', () => {
			if ( select.value === option.value ) {
				return;
			}

			select.value = option.value;
			select.dispatchEvent( new Event( 'change', { bubbles: true } ) );

			pillList.querySelectorAll( '.craftis-variation-pills__item' ).forEach( ( item ) => {
				item.classList.remove( 'is-selected' );
				item.setAttribute( 'aria-checked', 'false' );
			} );

			pill.classList.add( 'is-selected' );
			pill.setAttribute( 'aria-checked', 'true' );
		} );

		pillList.appendChild( pill );
	} );

	select.classList.add( 'craftis-variation-pills__native-select' );
	wrapper.appendChild( pillList );

	// Keeps pills in sync however the select's value changes elsewhere —
	// e.g. the "Clear" reset_variations link, which resets the <select>
	// directly rather than going through a pill click.
	select.addEventListener( 'change', () => {
		pillList.querySelectorAll( '.craftis-variation-pills__item' ).forEach( ( item ) => {
			const isSelected = item.dataset.value === select.value;
			item.classList.toggle( 'is-selected', isSelected );
			item.setAttribute( 'aria-checked', isSelected ? 'true' : 'false' );
		} );
	} );
}

const EXCLUDED_ATTRIBUTE_IDS = [ 'pa_color', 'pa_embroidery-type' ];

function initVariationPills() {
	document
		.querySelectorAll( 'table.variations select' )
		.forEach( ( select ) => {
			const attributeName = select.dataset.attributeName || '';
			const isExcluded = EXCLUDED_ATTRIBUTE_IDS.some(
				( id ) => select.id === id || attributeName === `attribute_${ id }`
			);

			if ( ! isExcluded ) {
				buildPills( select );
			}
		} );
}

document.addEventListener( 'DOMContentLoaded', initVariationPills );
document.body.addEventListener( 'wc_variation_form', initVariationPills );
